import { useEffect, useRef, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import type { ShopSummary } from '../../api/types.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { actionError } from '../../components/actionError.ts';
import { useFullScreenDialog } from '../../hooks/useFullScreenDialog.ts';
import { bpPercentHint, minorToMajorString } from '../config/configValidation.ts';
import {
  buildShopBillingPatch,
  initialShopBillingForm,
  validateBillingCommission,
  validateBillingDeviceFee,
  validateShopBillingForm,
  type ShopBillingFormState,
} from './shopBillingHelpers.ts';

export interface ShopBillingDialogProps {
  open: boolean;
  shop: ShopSummary;
  onClose: () => void;
  onSaved: () => void;
}

export function ShopBillingDialog({
  open,
  shop,
  onClose,
  onSaved,
}: Readonly<ShopBillingDialogProps>): React.JSX.Element {
  const { api } = useAuth();
  const fullScreen = useFullScreenDialog();
  const [form, setForm] = useState<ShopBillingFormState>(() => initialShopBillingForm(shop));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setForm(initialShopBillingForm(shop));
      setError('');
      setBusy(false);
      submittingRef.current = false;
    }
  }, [open, shop]);

  function set<K extends keyof ShopBillingFormState>(key: K, value: ShopBillingFormState[K]): void {
    setError('');
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function submit(): Promise<void> {
    if (submittingRef.current) return;
    const problem = validateShopBillingForm(form);
    if (problem !== null) {
      setError(problem);
      return;
    }

    const patch = buildShopBillingPatch(form, shop);
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }

    submittingRef.current = true;
    setBusy(true);
    setError('');
    try {
      await api.shops.updateBilling(shop.id, patch);
      onSaved();
      onClose();
    } catch (e) {
      setError(actionError(e));
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  }

  const commissionProblem = validateBillingCommission(form.commission);
  const deviceFeeProblem = validateBillingDeviceFee(form.deviceFee);
  const commissionHint = form.commission.trim() === '' ? null : bpPercentHint(form.commission);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" fullScreen={fullScreen}>
      <DialogTitle>Edit 1NL billing</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            label="Commission rate (bp)"
            value={form.commission}
            onChange={(event) => set('commission', event.target.value)}
            fullWidth
            disabled={busy}
            error={commissionProblem !== null}
            helperText={
              commissionProblem ??
              [commissionHint, `Leave blank to use the default (${shop.commission_rate_bp_default / 100}%).`]
                .filter(Boolean)
                .join(' ')
            }
            slotProps={{
              htmlInput: {
                'aria-label': 'shop-billing-commission',
                inputMode: 'numeric',
              },
            }}
          />
          <TextField
            label="Device monthly fee"
            value={form.deviceFee}
            onChange={(event) => set('deviceFee', event.target.value)}
            fullWidth
            disabled={busy}
            error={deviceFeeProblem !== null}
            helperText={
              deviceFeeProblem ??
              `Leave blank to use the default (${minorToMajorString(shop.device_monthly_fee_minor_default)}).`
            }
            slotProps={{
              htmlInput: {
                'aria-label': 'shop-billing-device-fee',
                inputMode: 'decimal',
              },
            }}
          />
          {error && (
            <Alert role="alert" severity="error" sx={{ fontWeight: 700 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <QuietButton onClick={onClose} disabled={busy}>
          Cancel
        </QuietButton>
        <Button onClick={() => void submit()} disabled={busy}>
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ShopBillingDialog;
